// The Overview (pages/OverviewPage.jsx), the AI estate at a glance over the
// range, against the span before it: the same length, immediately before.
// One measure, traces, runs through the tiles, the running total and the
// agents; severity counts each trace once, at the worst its review found;
// investigations are the team's, as the API returns them to the reader.
// Pure: no network, no React. The reads are lib/overviewReads.js.
import { caseState } from "./findingsConsole.js";
import { FRAME_DAYS, frameDays } from "./findingsTime.js";
import { resolveAgent } from "./fleetConsole.js";
import { rangeSpan } from "./shell.js";

const HOUR = 3_600_000;
const DAY = 86_400_000;
export const LEVELS = ["critical", "high", "medium", "low"];
export const LEVEL_LABEL = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };
// The agents named in the table before the rest fold into rows.
export const TOP = 6;

const midnight = (ms) => new Date(ms).setHours(0, 0, 0, 0);
const hourOf = (ms) => new Date(ms).setMinutes(0, 0, 0);
const addDays = (ms, n) => { const d = new Date(ms); d.setDate(d.getDate() + n); return d.getTime(); };
// Local days from a's to b's, both counted.
const daysFrom = (a, b) => Math.round((midnight(b) - midnight(a)) / DAY) + 1;
const sum = (list, f = (x) => x) => list.reduce((n, x) => n + f(x), 0);
// An hourly count ([hour start, n] or { at }) belongs to a span when its
// hour's middle falls in it, the hour a span starts in included: the hours a
// range touches, as Findings counts them.
const inHours = (at, span) => at + HOUR / 2 >= hourOf(span.from) && at + HOUR / 2 <= span.to;

/**
 * The range and the span before it: as many local days before a range of
 * whole days, else as long. `days` are the local days the hourly reads cover,
 * the span before's and the range's; where the two do not fit the widest
 * window the analytics take (FRAME_DAYS), as at 90 days, there is no span
 * before (`before` null) and the days are the range's own. The running total
 * steps by `unit`: an hour for two days or less, else a day.
 */
export function windowsOf(range) {
  const { from, to, exact } = rangeSpan(range);
  const length = daysFrom(from, to);
  const before = exact ? { from: 2 * from - to - 1, to: from - 1 } : { from: addDays(from, -length), to: from - 1 };
  const reach = daysFrom(before.from, to);
  const compare = reach <= FRAME_DAYS;
  return {
    now: { from, to }, before: compare ? before : null, exact, length,
    days: frameDays(to, compare ? reach : length),
    unit: to + 1 - from <= 2 * DAY ? "hour" : "day",
  };
}

// The windows hold for the hour, so a read keyed on them is asked once, and
// the Overview and Fleet read the same range over the same windows.
const windowsCache = new Map();
/** A range's windows (windowsOf), as they stand this hour. */
export function windowsOfRange(range) {
  const key = `${range}|${new Date().setMinutes(0, 0, 0)}`;
  if (!windowsCache.has(key)) {
    windowsCache.clear();
    windowsCache.set(key, windowsOf(range));
  }
  return windowsCache.get(key);
}

/** Traces in a span from traces by hour ([hour start, n]); null where either is unknown. */
export function tracesIn(hourly, span) {
  if (!hourly || !span) return null;
  let n = 0;
  for (const [at, k] of hourly) if (inHours(at, span)) n += k;
  return n;
}

/**
 * The running total, unit by unit across the range (`now`), and across the
 * span before from its start (`before`, null without one): each unit's start
 * (`at`), its traces (`n`) and the total so far (`run`).
 */
export function runningTotal(hourly, w) {
  const key = w.unit === "hour" ? hourOf : midnight;
  const series = (span) => {
    const starts = [];
    for (let t = key(span.from); t <= span.to; t = w.unit === "hour" ? t + HOUR : addDays(t, 1)) starts.push(t);
    const index = new Map(starts.map((t, i) => [t, i]));
    const n = starts.map(() => 0);
    for (const [at, k] of hourly || []) {
      if (!inHours(at, span)) continue;
      const i = index.get(key(at + HOUR / 2));
      if (i != null) n[i] += k;
    }
    let run = 0;
    return starts.map((t, i) => ({ at: t, n: n[i], run: (run += n[i]) }));
  };
  return { now: series(w.now), before: w.before ? series(w.before) : null };
}

const norm = (v) => String(v ?? "").trim().toLowerCase();
const pairOf = (r) => `${norm(r.surface)}\u0000${norm(r.provider)}`;
export const isNewAgent = (a) => a.before === 0 && a.now > 0;

/** A whole number split by weights, by largest remainder, so the parts add up to it exactly. */
export function apportion(total, weights) {
  const whole = sum(weights);
  if (!whole) return weights.map(() => 0);
  const raw = weights.map((v) => (v / whole) * total);
  const out = raw.map(Math.floor);
  const left = total - sum(out);
  raw.map((r, i) => [r - out[i], i]).sort((a, b) => b[0] - a[0]).slice(0, Math.max(0, left)).forEach(([, i]) => { out[i] += 1; });
  return out;
}

/**
 * The agents traces came from in the range and the span before (`before`
 * null without one), as Fleet names them (lib/fleetConsole.js resolveAgent),
 * the most traces first. A trace has one surface and one provider, so its
 * (surface, provider) group counts it once. The name can turn on what the
 * trace's frames reported (its agent type), so `names` gives, for each span,
 * Fleet's own groups of its traces, whose name each group is read by. A
 * group of one agent counts its traces exactly; one whose frames name more
 * than one is split between them as Fleet's groups split it.
 */
export function agentsOf({ now = [], before = null, names = {} } = {}) {
  const out = new Map();
  const add = (rows, groups, field) => {
    const by = new Map();
    for (const g of groups || []) {
      const a = resolveAgent(g);
      if (!by.has(pairOf(g))) by.set(pairOf(g), new Map());
      const named = by.get(pairOf(g));
      named.set(a.key, { ...a, n: (named.get(a.key)?.n || 0) + (Number(g.sessions) || 0) });
    }
    for (const r of rows) {
      // ponytail: a group split between agents is split by Fleet's counts,
      // which count a trace whose frames name two agents in both; exact in
      // total, near for each. Read by agent per trace if that ever matters.
      const named = [...(by.get(pairOf(r))?.values() || [])].filter((a) => a.n > 0);
      const parts = named.length ? named : [{ ...resolveAgent(r), n: 1 }];
      const split = apportion(Number(r.traces) || 0, parts.map((a) => a.n));
      parts.forEach((a, i) => {
        if (!out.has(a.key)) out.set(a.key, { key: a.key, name: a.name, now: 0, before: before ? 0 : null });
        out.get(a.key)[field] += split[i];
      });
    }
  };
  add(now, names.now, "now");
  if (before) add(before, names.before, "before");
  return [...out.values()].sort((a, b) => b.now - a.now || (b.before ?? 0) - (a.before ?? 0) || a.name.localeCompare(b.name));
}

/** The agents tile: agents with traces in the range, the new among them, and those with traces in the span before; null each where there is no span before. */
export function agentCounts(agents, compare) {
  return {
    now: agents.filter((a) => a.now > 0).length,
    new: compare ? agents.filter(isNewAgent).length : null,
    before: compare ? agents.filter((a) => a.before > 0).length : null,
    gone: compare ? agents.filter((a) => a.before > 0 && !a.now).length : null,
  };
}

/** Parts of a whole in tenths of a percent, by largest remainder, so the parts printed make exactly 100%. */
export const sharesOf = (values, total) => (total ? apportion(1000, values) : values.map(() => 0));

/**
 * The agents table in `slots` rows at most, Total included: the agents with
 * the most traces, up to TOP, as many as the rows hold; then the new ones
 * among the rest (`fresh`), while two agents or more are named; then the
 * others, whose traces before include those of agents with none since
 * (`gone`); then the total, which the rows always add up to. `rows` is how
 * many it takes; `max` is the scale every bar and the span before's tick are
 * drawn on.
 */
export function agentsTable(agents, compare, slots = Infinity) {
  const live = agents.filter((a) => a.now > 0);
  const cut = (top) => {
    const rest = live.slice(top);
    const fresh = top >= 2 ? rest.filter(isNewAgent) : [];
    const others = top >= 2 ? rest.filter((a) => !isNewAgent(a)) : rest;
    return { top, fresh, others, rows: top + (fresh.length ? 1 : 0) + (others.length ? 1 : 0) + 1 };
  };
  let fit = cut(Math.min(TOP, live.length));
  while (fit.top > 0 && fit.rows > slots) fit = cut(fit.top - 1);
  const { fresh, others } = fit;
  const lead = live.slice(0, fit.top);
  const gone = compare ? agents.filter((a) => a.before > 0 && !a.now) : [];
  const total = { now: sum(live, (a) => a.now), before: compare ? sum(agents, (a) => a.before) : null };
  const freshNow = sum(fresh, (a) => a.now);
  const othersNow = sum(others, (a) => a.now);
  const othersBefore = compare ? total.before - sum(lead, (a) => a.before) : null;
  const shares = sharesOf([...lead.map((a) => a.now), freshNow, othersNow], total.now);
  return {
    lead: lead.map((a, i) => ({ ...a, isNew: isNewAgent(a), share: shares[i] })),
    fresh: { agents: fresh, now: freshNow, share: shares[lead.length] },
    others: { count: others.length, now: othersNow, before: othersBefore, share: shares[lead.length + 1] },
    gone: { count: gone.length, before: sum(gone, (a) => a.before) },
    total,
    max: Math.max(1, ...lead.flatMap((a) => [a.now, a.before ?? 0]), freshNow, othersNow, othersBefore ?? 0),
    rows: fit.rows,
  };
}

/**
 * The chart and the tables in the room the window leaves them (`room`, px):
 * the chart takes what the tables leave it. Under its least (`L.chart[0]`)
 * the rows tighten (`row`, from `L.row[1]` down to `L.row[0]`), then the
 * agents table names fewer agents (`slots`, the rows it may take, Total
 * included; `rows(slots)` is how many it takes), then the chart shrinks to
 * its floor (`L.chart[1]`), and under that it goes. Side by side, the severity
 * rows and the cards stand beside the agents table, which is as tall; stacked
 * (`L.stacked`), below it, and the table goes when it cannot name one agent.
 * With nothing else to give, the rows tighten to `L.row[2]`, and last the
 * cards go (`cards`), as on a phone held sideways. The rest of `L` is the
 * blocks' heights in px: a table's head; the gaps over the chart, the tables
 * and, stacked, the severity rows; over the cards; and the cards.
 */
export function fitRoom(room, rows, L) {
  const full = rows(Infinity);
  for (const [drawn, cards] of [[true, true], [false, true], [false, false]]) {
    const fit = (r, least = L.row[0]) => Math.max(least, Math.min(L.row[1], Math.floor(r)));
    const severity = (row) => L.head + 6 * row + (cards ? L.cardsGap + L.card : 0);
    const least = drawn ? L.chart[0] : 0;
    const avail = room - (drawn ? L.chartGap : 0) - L.gridGap - (L.stacked ? L.sevGap : 0);
    let row, slots, used;
    if (L.stacked) {
      row = fit((avail - least - L.head - severity(0)) / (6 + full));
      slots = Math.floor((avail - least - L.head - severity(row)) / row);
      if (slots < 3) {
        if (drawn) continue;
        slots = 0;
        row = fit((avail - severity(0)) / 6, L.row[2]);
      }
      used = severity(row) + (slots ? L.head + rows(slots) * row : 0);
    } else {
      const g = avail - least;
      row = fit(Math.min((g - severity(0)) / 6, (g - L.head) / full), drawn ? L.row[0] : L.row[2]);
      slots = Math.floor((Math.max(severity(row), g) - L.head) / row);
      used = Math.max(severity(row), L.head + rows(slots) * row);
    }
    if (drawn ? avail - used >= L.chart[1] : used <= avail || !cards) return { row, slots, chart: drawn ? avail - used : 0, cards };
  }
}

/**
 * Traces by the worst behaviour their review found, over a span, from traces
 * by hour with those at least as bad as each level (lib/overviewReads.js):
 * each trace once, at its worst. `flagged` is the four levels together and
 * `unreviewed` the traces with no review yet. Null where either is unknown.
 */
export function severityIn(rows, span) {
  if (!rows || !span) return null;
  const t = { traces: 0, reviewed: 0, critical: 0, high: 0, medium: 0, low: 0 };
  for (const r of rows) if (inHours(r.at, span)) for (const k of Object.keys(t)) t[k] += r[k] || 0;
  return {
    critical: t.critical, high: t.high - t.critical, medium: t.medium - t.high, low: t.low - t.medium,
    flagged: t.low, unreviewed: t.traces - t.reviewed, traces: t.traces,
  };
}

/** A change as the page writes it: an arrow and how much (`fmt`), "no change", or nothing where the before is unknown. */
export function moveText(now, before, fmt = String) {
  if (now == null || before == null) return "";
  const d = now - before;
  return d ? `${d > 0 ? "↑" : "↓"} ${fmt(Math.abs(d))}` : "no change";
}

/** A change as a percentage of the before, or nothing where there was none before. */
export function pctText(now, before) {
  if (now == null || !before) return "";
  if (now === before) return "no change";
  const p = Math.round(Math.abs(now / before - 1) * 100);
  return `${now > before ? "↑" : "↓"} ${p ? `${p}%` : "<1%"}`;
}

const FATE = { investigating: "investigating", tp: "truePositive", joined: "joined", closed: "closed", new: "waiting" };

/**
 * The investigation cards, from every investigation the reader may see
 * (lib/useConsoleInvestigations.js listAllInvestigations, by id): those
 * opened in the range and in the span before; those opened in the range and
 * still investigated (open, with an owner) and the people who hold them; true
 * and false positives closed in the range and in the span before; what became
 * of those opened in the range (`fate`, lib/findingsConsole.js caseState:
 * investigating, true positive, joined, otherwise closed, or open with nobody
 * holding it); and those opened step by step, a week at a time (a day under
 * two weeks), the last step `partial` where unfinished, with the span
 * before's level per step (`levelBefore`). Counts for the span before are
 * null where there is none.
 */
export function investigationsIn(byId, w, nowMs = Date.now()) {
  const inSpan = (ms, s) => !!s && Number.isFinite(ms) && ms >= s.from && ms <= s.to;
  const zero = w.before ? 0 : null;
  const out = {
    opened: 0, openedBefore: zero, investigating: 0, people: 0,
    truePositive: 0, truePositiveBefore: zero, falsePositive: 0, falsePositiveBefore: zero,
    fate: { investigating: 0, truePositive: 0, joined: 0, closed: 0, waiting: 0 },
  };
  const owners = new Set();
  const created = [];
  for (const inv of byId.values()) {
    if (inSpan(inv.createdAtMs, w.now)) {
      const { state, owner } = caseState(inv);
      out.opened += 1;
      out.fate[FATE[state]] += 1;
      created.push(inv.createdAtMs);
      if (state === "investigating") {
        out.investigating += 1;
        owners.add(owner);
      }
    }
    if (inSpan(inv.createdAtMs, w.before)) out.openedBefore += 1;
    const verdict = inv.status === "closed" ? { true_positive: "truePositive", false_positive: "falsePositive" }[inv.verdict] : null;
    if (verdict && inSpan(inv.closedAtMs, w.now)) out[verdict] += 1;
    if (verdict && inSpan(inv.closedAtMs, w.before)) out[`${verdict}Before`] += 1;
  }
  out.people = owners.size;
  const step = w.length >= 14 ? 7 : 1;
  out.step = step;
  out.steps = [];
  for (let s = w.now.from; s <= w.now.to; s = addDays(s, step)) {
    const end = Math.min(addDays(s, step) - 1, w.now.to);
    out.steps.push({ from: s, to: end, n: created.filter((ms) => ms >= s && ms <= end).length, partial: end < addDays(s, step) - 1 || end >= nowMs });
  }
  out.levelBefore = w.before ? (out.openedBefore * step * DAY) / (w.before.to + 1 - w.before.from) : null;
  return out;
}

const NUM = new Intl.NumberFormat("en-US");
export const fmt = (n) => NUM.format(Math.round(n));

/** A count in one letter: 3.9M, 38k, 1.5k; under a thousand as it is. */
export function compact(n) {
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(/\.0$/, "")}M`;
  if (a >= 1e4) return `${Math.round(n / 1e3)}k`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1).replace(/\.0$/, "")}k`;
  return fmt(n);
}

/**
 * The page's numbers: exact, until the range holds a hundred thousand traces
 * or more (`big`); then a count of five figures and up, or one shown among
 * such counts (`group`), is one letter, and its hover gives it exactly.
 */
export const numberer = (big) => (v, group = v) => (big && Math.abs(group) >= 1e4 ? compact(v) : fmt(v));
